"use client";

import styles from "./index.module.scss";
import ModalSheet from "@/components/ModalSheet/ModalSheet";
import PromoCard from "@/domains/auth/components/registration/promo/components/promo-card";
import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useTranslations } from "next-intl";
import { IPromo, IPromoProps, PROMOTION_ACTION } from "@/domains/promotion/types";
import { promotionApi } from "@/domains/promotion/service";
import { PUBLIC_IMAGE_URL } from "@/network/http-config";
import { useQueryParam } from "@/hooks/use-query-param";

export default function Promo({ setPromoId, promoId, isOpen, onClose }: IPromoProps) {
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1120px)");
  const [promoList, setPromoList] = useState<IPromo[]>([]);
  const [moreDetail, setMoreDetail] = useState<IPromo | null>(null);
  const tPromo = useTranslations("AuthRegistrationPromo");
  const { getParam, deleteParam } = useQueryParam();

  const handleMoreDetail = (item: IPromo | null) => {
    setMoreDetail(item);
  };

  const handleActivePromo = (id: string | null) => {
    setPromoId(id);
  };

  const handleActivePromoDetail = useCallback(
    (value: string) => {
      handleActivePromo(value !== promoId ? value : null);
    },
    [promoId, handleActivePromo],
  );

  const fetchPromotions = useCallback(async () => {
    try {
      const response = await promotionApi.list({
        params: {
          action: PROMOTION_ACTION.registration,
        },
      });

      if (response.status !== 200) {
        throw new Error("PROMO IS NOT AVAILABLE");
      }

      const data = await response.data;

      if (getParam("auto-active-promo")) {
        setPromoId(data[0].id);

        deleteParam("auto-active-promo");
      }

      setPromoList(data);
    } catch (error) {
      console.error(error);
    }
  }, [promotionApi.list, setPromoList]);

  useEffect(() => {
    fetchPromotions();
  }, []);

  if (isTabletOrMobile) {
    return (
      <ModalSheet
        key={"modal-sheet-is-tablet-or-mobile"}
        isOpen={isOpen}
        onClose={() => onClose()}
        classNameWrapper={styles.modal_sheet__wrapper}
        classNameContainer={styles.modal_sheet__container}
        classNameContent={styles.modal_sheet__content}
      >
        {moreDetail ? (
          <>
            <div className={styles.modal_sheet__wrap}>
              <div className={styles.bonuses_detail__title}>
                <div className={styles.bonuses_detail__back} onClick={() => setMoreDetail(null)}>
                  <svg
                    viewBox="0 0 7 12"
                    fill="currentColor"
                    width="100%"
                    height="18px"
                    className={styles.bonuses_detail__back_icon}
                  >
                    <use href="/images/sprite.svg#arrow-1-left"></use>
                  </svg>
                </div>
                {moreDetail.title}
              </div>
              <div className={styles.bonuses_detail__body}>
                <div className={`${styles.bonuses_detail__body_img}`}>
                  <img
                    src={`${PUBLIC_IMAGE_URL}${moreDetail.imageSmall || "uploads/promotions/bonus.webp"}`}
                    width={132}
                    height={132}
                    alt="bonus"
                  />
                </div>

                <div className={styles.bonuses_detail__body_title}>{tPromo("detailDesc")}</div>

                <div className={styles.bonuses_detail__body_properties}>
                  <div className={styles.bonuses_detail__body_properties_item}>
                    <div className={styles.bonuses_detail__body_properties_title}>
                      {tPromo("maxAmount")}
                    </div>
                    <div className={styles.bonuses_detail__body_properties_value}>
                      {moreDetail.maxBonus}&nbsp;
                      <span className={styles.bonuses_detail__body_properties_currecy}>USDT</span>
                    </div>
                  </div>

                  <div className={styles.bonuses_detail__body_properties}>
                    <div className={styles.bonuses_detail__body_properties_item}>
                      <div className={styles.bonuses_detail__body_properties_title}>
                        {tPromo("minAmount")}
                      </div>
                      <div className={styles.bonuses_detail__body_properties_value}>
                        {moreDetail.minDeposit}&nbsp;
                        <span className={styles.bonuses_detail__body_properties_currecy}>USDT</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.bonuses_detail__body_properties}>
                    <div className={styles.bonuses_detail__body_properties_item}>
                      <div className={styles.bonuses_detail__body_properties_title}>
                        {tPromo("wager")}
                      </div>
                      <div className={styles.bonuses_detail__body_properties_value}>
                        x{moreDetail.wager}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.bonuses_detail__btn_wrap}>
                  <button
                    className={`${styles.bonuses_detail__btn} ${styles.bonuses_detail__btn_accent} ${promoId === moreDetail.id && styles.active}`}
                    onClick={() => handleActivePromoDetail(moreDetail.id)}
                  >
                    {promoId === moreDetail.id ? tPromo("cancel") : tPromo("apply")}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.bonuses__title}>{tPromo("title")}</div>

            <ul className={styles.bonuses__list}>
              {promoList &&
                promoList.map((item) => {
                  return (
                    <li key={item.id}>
                      <PromoCard
                        promo={item}
                        onActivePromo={handleActivePromo}
                        promoId={promoId}
                        onMoreDetail={handleMoreDetail}
                      />
                    </li>
                  );
                })}
            </ul>
          </>
        )}
      </ModalSheet>
    );
  }
  return (
    <div className={styles.bonuses} key={"bonuses-is-desktop"}>
      {moreDetail ? (
        <>
          <div className={styles.bonuses__wrapper_detail}>
            <div className={styles.bonuses_detail__title}>
              <div className={styles.bonuses_detail__back} onClick={() => setMoreDetail(null)}>
                <svg
                  viewBox="0 0 7 12"
                  fill="currentColor"
                  width="100%"
                  height="18px"
                  className={styles.bonuses_detail__back_icon}
                >
                  <use href="/images/sprite.svg#arrow-1-left"></use>
                </svg>
              </div>
              {moreDetail.title}
            </div>
            <div className={styles.bonuses_detail__body}>
              <div className={`${styles.bonuses_detail__body_img}`}>
                <img
                  src={`${PUBLIC_IMAGE_URL}${moreDetail.imageSmall || "uploads/promotions/bonus.webp"}`}
                  width={132}
                  height={132}
                  alt="bonus"
                />
              </div>

              <div className={styles.bonuses_detail__body_title}>{tPromo("detailDesc")}</div>

              <div className={styles.bonuses_detail__body_properties}>
                <div className={styles.bonuses_detail__body_properties_item}>
                  <div className={styles.bonuses_detail__body_properties_title}>
                    {tPromo("maxAmount")}
                  </div>
                  <div className={styles.bonuses_detail__body_properties_value}>
                    {moreDetail.maxBonus}&nbsp;
                    <span className={styles.bonuses_detail__body_properties_currecy}>USDT</span>
                  </div>
                </div>

                <div className={styles.bonuses_detail__body_properties}>
                  <div className={styles.bonuses_detail__body_properties_item}>
                    <div className={styles.bonuses_detail__body_properties_title}>
                      {tPromo("minAmount")}
                    </div>
                    <div className={styles.bonuses_detail__body_properties_value}>
                      {moreDetail.minDeposit}&nbsp;
                      <span className={styles.bonuses_detail__body_properties_currecy}>USDT</span>
                    </div>
                  </div>
                </div>

                <div className={styles.bonuses_detail__body_properties}>
                  <div className={styles.bonuses_detail__body_properties_item}>
                    <div className={styles.bonuses_detail__body_properties_title}>
                      {tPromo("wager")}
                    </div>
                    <div className={styles.bonuses_detail__body_properties_value}>
                      x{moreDetail.wager}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.bonuses_detail__btn_wrap}>
                <button
                  className={`${styles.bonuses_detail__btn} ${styles.bonuses_detail__btn_accent} ${promoId === moreDetail.id && styles.active}`}
                  onClick={() => handleActivePromoDetail(moreDetail.id)}
                >
                  {promoId === moreDetail.id ? tPromo("cancel") : tPromo("apply")}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.bonuses__wrapper}>
            <div className={styles.bonuses__title}>{tPromo("title")}</div>
            <ul className={styles.bonuses__list}>
              {promoList &&
                promoList.map((item) => {
                  return (
                    <li key={item.id}>
                      <PromoCard
                        promo={item}
                        onActivePromo={handleActivePromo}
                        promoId={promoId}
                        onMoreDetail={handleMoreDetail}
                      />
                    </li>
                  );
                })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
